import type { AssuranceViewDefinition, AssuranceViewGroup } from "./types";

/**
 * The 20 assurance views, grouped for navigation.
 *
 * View ids are a stable public contract: they appear in ?view= deep links,
 * captures, and external documents. Never rename or remove an id — regroup
 * or relabel instead.
 */
export const ASSURANCE_VIEW_GROUPS: AssuranceViewGroup[] = [
  "Executive overview",
  "Evidence & architecture",
  "Findings & remediation",
  "Supply chain & engineering",
  "Governance & policy",
  "Reports & proof-of-value",
];

export const ASSURANCE_VIEWS: AssuranceViewDefinition[] = [
  // ---- Executive overview ----
  {
    id: "application",
    label: "Application assurance",
    shortLabel: "Application",
    description:
      "Executive posture for the selected application: risk, exposure, release decision, and what drives them.",
    group: "Executive overview",
    aliases: ["applicationAssurance", "summary", "overview", "posture"],
  },
  {
    id: "portfolio",
    label: "Portfolio posture",
    shortLabel: "Portfolio",
    description:
      "Risk, evidence freshness, and assurance coverage across every application in scope.",
    group: "Executive overview",
    aliases: ["portfolioPosture", "portfolio", "applications", "repositories"],
  },
  {
    id: "capability",
    label: "Capability evidence",
    shortLabel: "Capabilities",
    description:
      "Which assurance capabilities are demonstrated with evidence, and which still require validation.",
    group: "Executive overview",
    aliases: ["capabilityParity", "capabilities", "capabilityEvidenceMatrix"],
  },

  // ---- Evidence & architecture ----
  {
    id: "coverage",
    label: "Coverage & provenance",
    shortLabel: "Coverage",
    description:
      "What was scanned, what was excluded and why, and which pipeline components produced each conclusion.",
    group: "Evidence & architecture",
    aliases: ["coverageProvenance", "coverage", "provenance", "scan"],
  },
  {
    id: "architecture",
    label: "Architecture",
    shortLabel: "Architecture",
    description:
      "Observed services, trust boundaries, data flows, and the attack surfaces they expose.",
    group: "Evidence & architecture",
    aliases: ["architectureAssurance", "architecture", "components", "services"],
  },

  // ---- Findings & remediation ----
  {
    id: "findings",
    label: "Findings inventory",
    shortLabel: "Findings",
    description:
      "The normalized finding inventory across every scanner domain, with filters and evidence links.",
    group: "Findings & remediation",
    aliases: ["findings", "findingInventory", "issues", "vulnerabilities"],
  },
  {
    id: "evidence",
    label: "Finding evidence",
    shortLabel: "Evidence",
    description:
      "Full evidence for one finding: trace, exploit scenario, impact, validation steps, and audit trail.",
    group: "Findings & remediation",
    aliases: ["findingEvidence", "evidence", "provenance"],
  },
  {
    id: "remediation",
    label: "Remediation",
    shortLabel: "Remediation",
    description:
      "Prioritized fixes with owners, effort, and the success criteria that close each workstream.",
    group: "Findings & remediation",
    aliases: ["remediation", "remediationPlan", "remediationCampaign", "fixPlan"],
  },
  {
    id: "campaign",
    label: "30 / 60 / 90 campaign",
    shortLabel: "30 / 60 / 90",
    description:
      "The remediation campaign sequenced into accountable 30, 60, and 90-day outcomes.",
    group: "Findings & remediation",
    aliases: ["campaign", "campaignPlan", "thirtySixtyNinety", "roadmap"],
  },

  // ---- Supply chain & engineering ----
  {
    id: "supply-chain",
    label: "Supply chain & SBOM",
    shortLabel: "Supply chain",
    description:
      "Dependency inventory, vulnerable and outdated components, and commit-bound SBOM provenance.",
    group: "Supply chain & engineering",
    aliases: ["supplyChain", "sbom", "dependencies", "dependencySummary"],
  },
  {
    id: "licenses",
    label: "OSS licenses",
    shortLabel: "Licenses",
    description:
      "License families, policy decisions, and the obligations that require legal review.",
    group: "Supply chain & engineering",
    aliases: ["ossLicenses", "licenses", "licenseCompliance"],
  },
  {
    id: "cloud",
    label: "Cloud & containers",
    shortLabel: "Cloud",
    description:
      "Infrastructure-as-code and container findings, image inventory, and runtime validation status.",
    group: "Supply chain & engineering",
    aliases: ["cloudContainers", "cloud", "containers", "infrastructure"],
  },
  {
    id: "quality",
    label: "Code quality & debt",
    shortLabel: "Quality",
    description:
      "Maintainability, complexity hotspots, duplication, and the modeled cost of carrying the debt.",
    group: "Supply chain & engineering",
    aliases: ["codeQualityDebt", "codeQuality", "quality", "technicalDebt"],
  },
  {
    id: "health",
    label: "Development health",
    shortLabel: "Dev health",
    description:
      "Delivery cadence, review flow, ownership concentration, and engineering sustainability risk.",
    group: "Supply chain & engineering",
    aliases: ["developmentHealth", "devHealth", "engineeringHealth"],
  },

  // ---- Governance & policy ----
  {
    id: "compliance",
    label: "Compliance mapping",
    shortLabel: "Compliance",
    description:
      "Measured evidence mapped to framework controls — and the gaps that block attestation.",
    group: "Governance & policy",
    aliases: ["compliance", "controlMapping", "complianceMappings", "frameworks"],
  },
  {
    id: "benchmark",
    label: "Benchmark",
    shortLabel: "Benchmark",
    description:
      "Position against the evaluation cohort, metric by metric, with the caveats that bound each claim.",
    group: "Governance & policy",
    aliases: ["benchmark", "benchmarks", "peerComparison"],
  },
  {
    id: "pr-guard",
    label: "PR guard",
    shortLabel: "PR guard",
    description:
      "Policy gates evaluated against current evidence, and the merge decision they produce.",
    group: "Governance & policy",
    aliases: ["prGuard", "pullRequestGuard", "policies", "policyGate"],
  },

  // ---- Reports & proof-of-value ----
  {
    id: "reports",
    label: "Report suite",
    shortLabel: "Reports",
    description:
      "Commit-bound reports, data exports, and SBOM artifacts served directly by the assurance API.",
    group: "Reports & proof-of-value",
    aliases: ["reportSuite", "reports", "exports", "downloads"],
  },
  {
    id: "deployment",
    label: "Deployment trust",
    shortLabel: "Deployment",
    description:
      "Build, artifact, and approval evidence connected to deployment confidence — including accepted risks.",
    group: "Reports & proof-of-value",
    aliases: ["deploymentTrust", "deployment", "auditDecisions", "artifactTrust"],
  },
  {
    id: "replacement",
    label: "Replacement proof-of-value",
    shortLabel: "Proof-of-value",
    description:
      "Measured coverage against replacement criteria, stated without overclaiming, with the validation path.",
    group: "Reports & proof-of-value",
    aliases: ["replacementProofOfValue", "proofOfValue", "replacement", "pov"],
  },
];

export const DEFAULT_ASSURANCE_VIEW = "application";

export function getAssuranceView(id: string | null): AssuranceViewDefinition {
  return (
    ASSURANCE_VIEWS.find((view) => view.id === id) ||
    ASSURANCE_VIEWS.find((view) => view.id === DEFAULT_ASSURANCE_VIEW) ||
    ASSURANCE_VIEWS[0]
  );
}

export function viewsInGroup(group: AssuranceViewGroup): AssuranceViewDefinition[] {
  return ASSURANCE_VIEWS.filter((view) => view.group === group);
}
